import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Breadcrumb from "@/components/Breadcrumb";
import SitterCard from "@/components/SitterCard";
import Seo, { breadcrumbSchema, localBusinessSchema, faqSchema } from "@/components/Seo";
import { api } from "@/lib/api";
import { CITY_META } from "@/data/cityMeta";
import { MapPin, ShieldCheck, Star, Search, Home as HomeIcon, Heart, Sparkles } from "lucide-react";

export default function CityLanding() {
  const params = useParams();
  const citySlug = (params.citySlug || params.id || "").toLowerCase();
  const meta = CITY_META[citySlug];
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data: d } = await api.get(`/seo/city/${citySlug}`); setData(d); }
      catch (_) { setData({ items: [], reviews: [], count: 0, rating_avg: 0, city: meta?.name || citySlug }); }
      setLoading(false);
    })();
  }, [citySlug, meta]);

  if (!meta && !loading && !data?.items?.length) {
    return (
      <div className="min-h-screen bg-linen">
        <Seo title={`Caregivers in ${citySlug}`} path={`/babysitters/${citySlug}`} noindex />
        <Nav />
        <main className="max-w-3xl mx-auto px-5 py-20 text-center">
          <h1 className="font-serif text-4xl text-forest">No caregivers here yet</h1>
          <p className="text-forest/70 mt-3">We're growing across Canada. New caregivers join every week.</p>
          <Link to="/browse" className="btn-primary mt-8 inline-block">Browse all caregivers</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const cityName = meta?.name || data?.city || citySlug;
  const path = `/babysitters/${citySlug}`;
  const description = meta?.intro
    ? meta.intro.slice(0, 155)
    : `${data?.count || 0} verified, CPR-certified babysitters in ${cityName}. Search by language, availability and rating on Sakeenah Circle.`;
  const faqs = meta?.faqs || [];
  const neighborhoods = meta?.neighborhoods || [];
  const testimonials = meta?.testimonials || [];
  const items = data?.items || [];
  const reviews = data?.reviews || [];

  return (
    <div className="min-h-screen bg-linen">
      <Seo
        title={meta?.h1 || `Babysitters in ${cityName}`}
        path={path}
        description={description}
        jsonLd={[
          breadcrumbSchema([{ name: "Find a caregiver", path: "/browse" }, { name: cityName, path }]),
          localBusinessSchema({ city: cityName, path, count: items.length, rating_avg: data?.rating_avg || 0 }),
          faqs.length ? faqSchema(faqs) : null,
        ].filter(Boolean)}
      />
      <Nav />
      <main className="max-w-7xl mx-auto px-5 sm:px-8 pt-8 pb-16">
        <Breadcrumb items={[{ name: "Find a caregiver", path: "/browse" }, { name: cityName }]} />

        {/* Hero */}
        <header className="mt-6 grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-8 space-y-5">
            <span className="badge-sage flex items-center gap-1 w-fit"><MapPin className="w-3 h-3" /> {cityName}, ON</span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-forest leading-[1.05] tracking-tight" data-testid="city-h1">
              {meta?.h1 || `Verified babysitters in ${cityName}`}
            </h1>
            <p className="text-forest/75 leading-relaxed max-w-2xl">{meta?.intro || description}</p>
            <div className="flex flex-wrap gap-4 text-sm text-forest/70 pt-1">
              <span className="flex items-center gap-1"><ShieldCheck className="w-4 h-4 text-gold" /> Police + CPR verified</span>
              <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-gold text-gold" /> {data?.rating_avg || "New"} avg rating</span>
              <span className="flex items-center gap-1"><Search className="w-4 h-4" /> {items.length} caregiver{items.length === 1 ? "" : "s"} available</span>
            </div>
            <div className="flex flex-wrap gap-3 pt-2">
              <Link to="/register" className="btn-primary">Find a caregiver</Link>
              <Link to="/browse" className="btn-outline">Browse all cities</Link>
            </div>
          </div>
          <aside className="md:col-span-4 card-organic p-6 space-y-4">
            <div className="flex items-center gap-2 text-gold">
              <Sparkles className="w-5 h-5" />
              <span className="font-serif text-xl text-forest">Why {cityName} families choose us</span>
            </div>
            <ul className="text-sm text-forest/80 space-y-2">
              <li className="flex gap-2"><ShieldCheck className="w-4 h-4 text-gold mt-0.5" /> Police-checked & CPR-certified</li>
              <li className="flex gap-2"><Heart className="w-4 h-4 text-gold mt-0.5" /> Multilingual & culturally aware</li>
              <li className="flex gap-2"><Star className="w-4 h-4 text-gold mt-0.5" /> Honest family reviews</li>
              <li className="flex gap-2"><HomeIcon className="w-4 h-4 text-gold mt-0.5" /> Serving every {cityName} neighbourhood</li>
            </ul>
          </aside>
        </header>

        {/* Sitter listings */}
        <section className="mt-16" aria-label={`Available babysitters in ${cityName}`} data-testid="city-sitters">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
            <h2 className="font-serif text-3xl text-forest">Available caregivers in {cityName}</h2>
            <Link to="/browse" className="text-sm text-forest/70 hover:text-forest underline underline-offset-4">See all →</Link>
          </div>
          {items.length === 0 ? (
            <div className="card-organic p-10 text-center">
              <p className="font-serif text-2xl text-forest">Our {cityName} circle is opening soon.</p>
              <p className="text-forest/70 mt-2 text-sm">Be the first to know when we onboard verified caregivers in {cityName}. Free accounts get email alerts.</p>
              <Link to="/register" className="btn-primary mt-6 inline-block">Create free account</Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((s) => <SitterCard key={s.id} s={s} />)}
            </div>
          )}
        </section>

        {/* Neighborhoods served */}
        {neighborhoods.length > 0 && (
          <section className="mt-16" aria-label={`${cityName} neighbourhoods served`} data-testid="city-neighborhoods">
            <div className="max-w-3xl">
              <span className="badge-gold">Neighbourhoods served</span>
              <h2 className="font-serif text-3xl text-forest mt-3">Sakeenah Circle across {cityName}</h2>
              <p className="text-forest/70 mt-2">Our caregivers live in and travel to every corner of {cityName}. Some of the areas we regularly serve:</p>
            </div>
            <div className="flex flex-wrap gap-2 mt-6">
              {neighborhoods.map((n) => (
                <span key={n} className="pill border border-taupe text-forest/80 bg-taupe/20">{n}</span>
              ))}
            </div>
          </section>
        )}

        {/* Reviews */}
        <section className="mt-16" aria-label={`Reviews from ${cityName} families`} data-testid="city-reviews">
          <div className="max-w-3xl">
            <span className="badge-sage">Family reviews</span>
            <h2 className="font-serif text-3xl text-forest mt-3">What {cityName} families say</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-8">
            {[
              ...reviews.map((r) => ({ q: r.text, by: `${r.parent_name} · via ${r.sitter_name}`, rating: r.rating })),
              ...testimonials.map((t) => ({ q: t.q, by: t.by, rating: 5 })),
            ].slice(0, 6).map((t, i) => (
              <article key={i} className="card-organic p-6">
                <div className="flex gap-1 text-gold">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className={`w-4 h-4 ${j < (t.rating || 5) ? "fill-gold text-gold" : "text-taupe"}`} />
                  ))}
                </div>
                <p className="font-serif text-lg text-forest mt-4 leading-relaxed">"{t.q}"</p>
                <div className="text-xs text-forest/60 mt-4">— {t.by}</div>
              </article>
            ))}
          </div>
        </section>

        {/* FAQ */}
        {faqs.length > 0 && (
          <section className="mt-16" aria-label={`FAQ about babysitters in ${cityName}`} data-testid="city-faq">
            <div className="max-w-3xl">
              <span className="badge-sage">FAQ</span>
              <h2 className="font-serif text-3xl text-forest mt-3">Common questions from {cityName} families</h2>
            </div>
            <div className="space-y-3 mt-8">
              {faqs.map((f, i) => (
                <article key={i} className="card-organic p-6">
                  <h3 className="font-serif text-lg text-forest">{f.q}</h3>
                  <p className="text-sm text-forest/75 leading-relaxed mt-2">{f.a}</p>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* Cross-city links */}
        <section className="mt-16 card-organic p-8 md:p-10" aria-label="Nearby cities">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-serif text-3xl text-forest">Looking beyond {cityName}?</h2>
            <p className="text-forest/70 mt-2">Sakeenah Circle serves families across the GTA.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-8">
            {Object.values(CITY_META).filter((c) => c.slug !== citySlug).map((c) => (
              <Link key={c.slug} to={`/babysitters/${c.slug}`} className="text-center p-4 rounded-2xl border border-taupe hover:border-forest transition-colors">
                <div className="font-serif text-lg text-forest">{c.name}</div>
                <div className="text-xs text-forest/60 mt-1">Verified caregivers</div>
              </Link>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
